// src/app/components/call/call-overlay.ts

import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { CallService, ICallSession } from '../../shared/services/call.service';

@Component({
  selector:        'app-call-overlay',
  standalone:      true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports:         [CommonModule],
  templateUrl:     './call-overlay.html',
})
export class CallOverlay implements OnInit, OnDestroy, AfterViewChecked {

  private callService = inject(CallService);
  private cdr         = inject(ChangeDetectorRef);

  @ViewChild('localVideo')  localVideoRef!:  ElementRef<HTMLVideoElement>;
  @ViewChild('remoteVideo') remoteVideoRef!: ElementRef<HTMLVideoElement>;

  readonly callSession$  = this.callService.callSession$;
  readonly localStream$  = this.callService.localStream$;
  readonly remoteStream$ = this.callService.remoteStream$;

  callDuration  = '00:00';
  errorMessage: string | null = null;
  hasRemoteVideo = false;
  callEndedToast: { duration: string; callType: string } | null = null;

  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private callStartTime: Date | null = null;
  private errorTimeout:      ReturnType<typeof setTimeout> | null = null;
  private callEndedTimeout:  ReturnType<typeof setTimeout> | null = null;
  private subs = new Subscription();

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  ngOnInit(): void {
    console.log('[CallOverlay] Initializing');
    // Signalling (socket connect + call listener registration) is now
    // triggered from the app root as soon as the user is authenticated —
    // see App.ngOnInit — so it's live on every page, not just this one.
    // Session state changes
    this.subs.add(
      this.callService.callSession$.subscribe(session => {
        if (session?.state === 'connected') {
          const newStartTime = session.startedAt ?? new Date();
          const changed = !this.callStartTime || newStartTime.getTime() !== this.callStartTime.getTime();
          this.callStartTime = newStartTime;
          // Start the ticking interval on first connect, or if the
          // authoritative 'call:started' timestamp corrected our start time —
          // either way we need it running with the right reference time.
          if (!this.timerInterval || changed) {
            this._startTimer();
          }
        }
        if (!session) {
          this._stopTimer();
          this.hasRemoteVideo = false;
          this.errorMessage   = null;
        }
        this.cdr.markForCheck();
      }),
    );

    // Local stream — attach to video element, forced muted (see _attachStream)
    this.subs.add(
      this.callService.localStream$.subscribe(stream => {
        this._attachStream(this.localVideoRef, stream, true);
        this.cdr.markForCheck();
      }),
    );

    // BUG FIX 4: Remote stream — track whether it has video tracks
    this.subs.add(
      this.callService.remoteStream$.subscribe(stream => {
        this._attachStream(this.remoteVideoRef, stream, false);
        this.hasRemoteVideo = !!stream && stream.getVideoTracks().length > 0;
        this.cdr.markForCheck();
      }),
    );

    // Error messages (permission denied, connection failed, etc.)
    this.subs.add(
      this.callService.callError$.subscribe(msg => {
        this.errorMessage = msg;
        this.cdr.markForCheck();
        // Auto-clear after 4s — clear any previous pending clear first so a
        // fast second error doesn't get its dismissal cancelled/overridden
        // by an earlier timeout firing at the wrong moment.
        if (this.errorTimeout) clearTimeout(this.errorTimeout);
        this.errorTimeout = setTimeout(() => {
          this.errorMessage  = null;
          this.errorTimeout  = null;
          this.cdr.markForCheck();
        }, 4000);
      }),
    );

    // Call ended toast — show in centre of screen for 4 s
    this.subs.add(
      this.callService.callEnded$.subscribe(({ duration, callType }) => {
        this.callEndedToast = { duration, callType };
        this.cdr.markForCheck();
        // Clear any previous pending dismissal before scheduling a new one —
        // without this, an overlapping/duplicate callEnded$ emission could
        // leave a stale timeout referencing an already-replaced toast, or
        // the timer could be lost entirely if the component were re-created
        // mid-flight, leaving the popup stuck until a manual page refresh.
        if (this.callEndedTimeout) clearTimeout(this.callEndedTimeout);
        this.callEndedTimeout = setTimeout(() => {
          this.callEndedToast    = null;
          this.callEndedTimeout  = null;
          this.cdr.markForCheck();
        }, 4000);
      }),
    );
  }

  ngAfterViewChecked(): void {
    // Re-attach if element becomes available after *ngIf renders
    this._attachStream(this.localVideoRef,  this.callService.localStream$.value, true);
    this._attachStream(this.remoteVideoRef, this.callService.remoteStream$.value, false);
  }

  ngOnDestroy(): void {
    this._stopTimer();
    if (this.errorTimeout)     clearTimeout(this.errorTimeout);
    if (this.callEndedTimeout) clearTimeout(this.callEndedTimeout);
    this.subs.unsubscribe();
  }

  // ─── Actions ──────────────────────────────────────────────────────────────

  answer():       void { this.callService.answerCall(); }
  reject():       void { this.callService.rejectCall(); }
  cancel():       void { this.callService.cancelCall(); }
  end():          void { this.callService.endCall(); }
  toggleMute():   void { this.callService.toggleMute(); }
  toggleCamera(): void { this.callService.toggleCamera(); }

  // ─── Template helpers ─────────────────────────────────────────────────────

  getCallLabel(session: ICallSession): string {
    switch (session.state) {
      case 'calling':   return session.remoteRinging ? 'Ringing…' : 'Calling…';
      case 'ringing':   return `Incoming ${session.callType === 'VIDEO' ? 'Video' : 'Voice'} Call`;
      case 'connected': return this.callDuration;
      default:          return '';
    }
  }

  isVideo(session: ICallSession): boolean {
    return session.callType === 'VIDEO';
  }

  getAvatar(session: ICallSession): string {
    return session.remoteUser.profileImage?.urlSmall
      ?? session.remoteUser.profileImage?.urlMedium
      ?? '/images/user/user-default.svg';
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  /**
   * Safely assign a stream to a video element only when both exist and differ.
   *
   * `isLocal` is required: Angular's static `muted` HTML attribute is not
   * reliably honoured once `srcObject` is assigned programmatically (same
   * quirk documented in video-capture.ts). Without forcing `.muted`/`.volume`
   * here in JS, the local element can end up unmuted — the mic's own audio
   * gets played back through the speakers, gets picked up by the mic again,
   * and the far end hears (and you hear) an echo. The remote element must
   * stay unmuted so the other participant's audio is actually heard.
   */
  private _attachStream(
    ref: ElementRef<HTMLVideoElement> | undefined,
    stream: MediaStream | null,
    isLocal: boolean,
  ): void {
    const el = ref?.nativeElement;
    if (!el) return;

    // Always enforce the correct muted state, even if srcObject is unchanged —
    // guards against the attribute/property mismatch regardless of ordering.
    el.muted  = isLocal;
    el.volume = isLocal ? 0 : 1;

    if (stream && el.srcObject !== stream) {
      el.srcObject = stream;
      el.autoplay   = true;
      el.playsInline = true;
      el.play().catch(() => { /* autoplay policy — user gesture required */ });
    } else if (!stream) {
      el.srcObject = null;
    }
  }

  private _startTimer(): void {
    // NOTE: do NOT call _stopTimer() here — it resets callStartTime to null,
    // which was set by the caller right before this method runs. That wiped
    // out the start time on every call, so the elapsed-time check below
    // always failed and the on-screen timer froze at 00:00. Only clear any
    // existing interval, leaving callStartTime untouched.
    if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null; }
    this.timerInterval = setInterval(() => {
      if (!this.callStartTime) return;
      const elapsed = Math.floor((Date.now() - this.callStartTime.getTime()) / 1000);
      const m = Math.floor(elapsed / 60).toString().padStart(2, '0');
      const s = (elapsed % 60).toString().padStart(2, '0');
      this.callDuration = `${m}:${s}`;
      this.cdr.markForCheck();
    }, 1_000);
  }

  private _stopTimer(): void {
    if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null; }
    this.callDuration  = '00:00';
    this.callStartTime = null;
  }
}